import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { ActivationPricingService } from '../../core/activation-pricing.service';
import { ActivationService } from '../../core/activation.service';
import { ActivatablePackage, SelectedItem, SelectedItems, SortDirection } from '../../core/interface';
import { pageArray } from '../../core/utils';

@Injectable()
export class PackageActivationTableService {
  readonly activatablePackages$: Observable<ActivatablePackage[]>;
  readonly hidePricing$: Observable<boolean>;
  readonly packagesHasRestrictions$: Observable<boolean>;
  readonly showingRestrictedPackages$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly totalPackages$: Observable<number>;
  readonly selectedItems$: Observable<SelectedItems>;

  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private nameSort$$: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>('ASCENDING');
  private paged$$: BehaviorSubject<PageEvent> = new BehaviorSubject<PageEvent>(undefined);

  constructor(
    private activationService: ActivationService,
    private activationPricingService: ActivationPricingService,
  ) {
    this.hidePricing$ = this.activationPricingService.hidePricing$;
    this.selectedItems$ = this.activationService.selectedItems$;
    const unsortedActivatablePackages$ = this.activationService.activatablePackages$;
    // searchTermDelayed$ used to reduce the number of times we search the list
    // (useful to not make repeated calls while a user is typing)
    const searchTermDelayed$ = this.searchTerm$$.pipe(debounceTime(200), distinctUntilChanged()) as Observable<string>;

    const searchedPackages$ = combineLatest([unsortedActivatablePackages$, searchTermDelayed$]).pipe(
      map(([pkgs, searchTerm]) => {
        if (!pkgs || !(pkgs.length > 0)) {
          return [];
        }
        searchTerm = searchTerm.trim().toUpperCase();
        return pkgs.filter((pkg) => {
          const uppercasePackageName = pkg.name ? pkg.name.toUpperCase() : '';
          const uppercasePackageId = pkg.packageId.toUpperCase();
          return uppercasePackageName.indexOf(searchTerm) >= 0 || uppercasePackageId.indexOf(searchTerm) >= 0;
        });
      }),
      shareReplay(1),
    );

    this.totalPackages$ = combineLatest([searchedPackages$, this.showingRestrictedPackages$]).pipe(
      map(([pkgs, showRestricted]) => {
        if (pkgs && pkgs.length > 0) {
          if (showRestricted) {
            return pkgs.length;
          } else {
            const notRestrictedPackages = pkgs.filter((pkg) => !pkg.restricted);
            return notRestrictedPackages ? notRestrictedPackages.length : 0;
          }
        } else {
          return 0;
        }
      }),
    );

    const sortedPackages$ = combineLatest([searchedPackages$, this.nameSort$$]).pipe(
      map(([packages, sortDirection]) => {
        if (!packages || !(packages.length > 0)) {
          return [];
        }
        return packages.sort((pkg1, pkg2) => {
          const pkg1Name = pkg1.name ? pkg1.name : '';
          const pkg2Name = pkg2.name ? pkg2.name : '';
          return sortDirection === 'ASCENDING' ? pkg1Name.localeCompare(pkg2Name) : pkg2Name.localeCompare(pkg1Name);
        });
      }),
      shareReplay(1),
    );

    const pagedPackages$ = combineLatest([sortedPackages$, this.paged$$]).pipe(
      map(([sortedPackages, pageEvent]) => {
        return pageArray(sortedPackages, pageEvent, 25);
      }),
    );

    this.activatablePackages$ = pagedPackages$;

    this.packagesHasRestrictions$ = unsortedActivatablePackages$.pipe(
      map((pkgs) => {
        return pkgs.some((pkg) => pkg.restricted);
      }),
    );
  }

  handlePackageCheckboxEvent(selectedItems: SelectedItem[]): void {
    this.activationService.selectItems(selectedItems);
  }

  packageSeachTermChanged(searchTerm: string): void {
    this.searchTerm$$.next(searchTerm);
  }

  packageSortTermChanges(order: SortDirection): void {
    this.nameSort$$.next(order);
  }

  packagePagingChanged(pageEvent: PageEvent): void {
    this.paged$$.next(pageEvent);
  }

  toggleShowingRestrictedPackages(): void {
    this.showingRestrictedPackages$.next(!this.showingRestrictedPackages$.getValue());
  }
}
